import React, { Component, Fragment } from 'react';
import { Container, Row, Col } from 'react-grid-system';

import classnames from 'classnames';
import Select from 'components/cosmos/select';
import { DatePicker } from 'components/date';
import { Dropzone } from 'components/dropzone';
import { Form, FormControl } from 'components/form';
import { Grid } from 'components/grid';
import Html from 'components/Html';
import { InputBlock } from 'components/input';
import { Loader } from 'components/loader';
import { translate } from 'components/translate';
import isNil from 'lodash/isNil';
import { inject } from 'mobx-react';
import { ClassesService } from 'modules/content';
import FileCard from 'modules/file/components/FileCard';
import { UiService } from 'modules/ui';
import UserService from 'modules/user/UserService';
import { ImportService } from 'modules/userManager/imports';
import PropTypes from 'prop-types';

import DescriptionImport from './DescriptionImport';
import SaveUsers from './SaveUsers';

import './Import.scss';

@translate()
@inject((stores) => ({
  limit: stores.UserStore.limit
}))
export default class BatchImport extends Component {
  static propTypes = {
    limit: PropTypes.number
  };

  state = {
    classesList: [],
    membershipClass: null,
    purchaseDate: '',
    csv: null,
    loaded: true
  };

  componentDidMount() {
    this.fetchData();
  }

  fetchData = () => {
    this.updateImportLimit();

    this.getClasses();
  };

  updateImportLimit() {
    ImportService.getImportLimit()
      .then((limit) => {
        UserService.initLimit(limit);
      })
      .catch(({ data = {} }) => {
        UserService.initLimit(0);
        UiService.handleError(data);
      });
  }

  getClasses() {
    ClassesService.getSelectFormatedClasses().then((classesList) => {
      const membershipClass = classesList[0] || '';

      this.setState({ classesList, membershipClass });
    });
  }

  setClass = (value) => {
    this.setState({
      membershipClass: value
    });
  };

  onDateChange = (date) => {
    this.setState({ purchaseDate: date });
  };

  onFilesChange = (csv, rejected) => {
    if (csv.length) {
      this.setState({ csv: csv[0] });
    } else {
      rejected = rejected[0];
      const format = '.csv';
      const firstIndexRejected = rejected.name.length - format.length;
      const isRejected =
        rejected.name.indexOf(format, firstIndexRejected) !== -1;

      if (isRejected) {
        this.setState({ csv: rejected });
      } else {
        UiService.setAlertError('empty_csv');
      }
    }
  };

  clearFile = () => {
    this.setState({ csv: null });
  };

  clearForm = () => {
    this.setState({ purchaseDate: '', csv: null });
  };

  BatchImport = async () => {
    const { csv, membershipClass, purchaseDate } = this.state;

    if (!csv) {
      UiService.setAlertError('empty_csv');

      return false;
    }

    this.toggleLoader();
    const data = { csv, membershipClass: membershipClass.value };

    if (purchaseDate) {
      data.purchaseDate = new Date(purchaseDate).toISOString();
    }

    ImportService.importCsv(data).then(() => {
      this.clearForm();
      this.updateImportLimit();

      this.toggleLoader();
    });
  };

  toggleLoader = () => {
    this.setState((prevState) => ({ loaded: !prevState.loaded }));
  };

  get renderCsv() {
    const { csv } = this.state;
    const { limit, t } = this.props;

    if (!csv) {
      return (
        <Dropzone
          className="mt__sm"
          onDrop={(accepted, rejected) =>
            this.onFilesChange(accepted, rejected)
          }
          disabled={!limit}
          multiple={false}
          accept="text/csv, application/vnd.ms-excel"
        >
          <div className="dropzone__info">
            <div className="dropzone__info__label">
              <label>{t('users_import.batch.coding.placeholder')}</label>
            </div>
            <div className="dropzone__action__button">
              <button className="hot-button hot-button--sm" disabled={!limit}>
                {t('general.choose_file')}
              </button>
            </div>
          </div>
        </Dropzone>
      );
    } else {
      return (
        <div className="files">
          <FileCard
            type={csv.type}
            size={csv.size}
            name={csv.name}
            removeFile={this.clearFile}
            enableVisualize={false}
          />
        </div>
      );
    }
  }

  render() {
    const { membershipClass, classesList, purchaseDate, loaded } = this.state;
    const { limit, t } = this.props;

    return (
      <Fragment>
        <Grid block>
          <h2>{t('users_import.batch.title')}</h2>
          <label className="label__subtitle mb__md">
            <Html>
              {t('users_import.batch.description', { checkOnlineEvent: true })}
            </Html>
          </label>
          <label className="label__subtitle mb__md">
            <Html>{t('users_import.batch.example')}</Html>
          </label>
          <label className="label__subtitle mb__md">
            {t('users_import.batch.examples.1')}
            <br />
            {t('users_import.batch.examples.2')}
            <br />
            {t('users_import.batch.examples.3')}
          </label>
          {!isNil(limit) && limit >= 0 && (
            <label className="label__subtitle mb__md">
              {t('users_import.batch.number_users_explanation', {
                checkOnlineEvent: true,
                data: { amount: limit }
              })}
            </label>
          )}

          <Form>
            <Container fluid className="users__form p__0">
              <Row>
                <Col xs={12} sm={6} md={6} lg={4}>
                  <Select
                    label={t('users_import.batch.class.label')}
                    name="class"
                    className={classnames(
                      'class__select users-manually__fields__input mb__sm',
                      { 'class__select--disabled': !limit }
                    )}
                    labelClassName="_mt-0"
                    value={membershipClass}
                    placeholder={t('users_import.batch.class.placeholder')}
                    options={classesList}
                    onChange={this.setClass}
                    disabled={!limit}
                  />
                </Col>
                <Col xs={12} sm={6} md={6} lg={4}>
                  <FormControl>
                    <InputBlock
                      name="date"
                      labelValue="users_import.batch.date.label"
                      placeholder="users_import.batch.date.placeholder"
                      className="users-manually__fields__input mb__sm"
                    >
                      <DatePicker
                        date={purchaseDate}
                        onDateChange={this.onDateChange}
                        className="users-manually__fields__input purchase__date"
                        isOutsideRange={ImportService.isOutsideRange}
                        disabled={!limit}
                      />
                    </InputBlock>
                  </FormControl>
                </Col>
              </Row>

              {this.renderCsv}
            </Container>
          </Form>
          {!loaded && <Loader mask />}
        </Grid>

        <DescriptionImport />

        <SaveUsers disabled={!loaded || !limit} onSubmit={this.BatchImport} />
      </Fragment>
    );
  }
}
